import React, { useState, useEffect, useCallback } from 'react';
import { CommentItem } from './CommentItem';
import { LoadingSpinner } from './ui';
import { commentService } from '../services/commentService';
import { useAuth } from '../hooks/useAuth';
import type { Comment } from '../types/comment';
import { Timestamp } from 'firebase/firestore';

interface CommentSectionProps {
  targetType: 'activity' | 'event';
  targetId: string;
  eventId: string;
  eventTitle: string;
  activityId?: string;
  activityType?: 'event_interested' | 'event_updated' | 'event_created';
  initialComments?: Comment[];
  showCommentCount?: boolean;
  collapsible?: boolean;
  maxInitialDisplay?: number;
}

export function CommentSection({
  targetType,
  targetId,
  eventId,
  eventTitle,
  activityId,
  activityType,
  initialComments = [],
  showCommentCount = true,
  collapsible = false,
  maxInitialDisplay = 3
}: CommentSectionProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(!collapsible);
  const [hasLoadedComments, setHasLoadedComments] = useState(initialComments.length > 0);

  // Load comments if not already loaded
  const loadComments = useCallback(async () => {
    if (hasLoadedComments) return;

    try {
      setLoading(true);
      const result = await commentService.getComments(targetType, targetId, {
        limitCount: 20
      });
      setComments(result.comments);
      setHasLoadedComments(true);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  }, [hasLoadedComments, targetType, targetId]);

  // Load comments on mount if not provided initially
  useEffect(() => {
    if (!hasLoadedComments) {
      loadComments();
    }
  }, [hasLoadedComments, loadComments]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !user) return;

    try {
      setIsSubmitting(true);
      const commentId = await commentService.createComment({
        targetType,
        targetId,
        userId: user.uid,
        userName: user.displayName || user.email || 'Anonymous',
        userAvatar: user.photoURL || undefined,
        content: commentText.trim(),
        eventId,
        eventTitle,
        activityId,
        activityType
      });

      // Add new comment to local state
      const newComment: Comment = {
        id: commentId,
        targetType,
        targetId,
        userId: user.uid,
        userName: user.displayName || user.email || 'Anonymous',
        userAvatar: user.photoURL || undefined,
        content: commentText.trim(),
        eventId,
        eventTitle,
        activityId,
        activityType,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        likesCount: 0,
        likedBy: []
      };

      setComments(prev => [newComment, ...prev]);
      setCommentText('');
      setShowAll(true); // Expand to show new comment
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    if (!user) return;
    try {
      const isLiked = await commentService.toggleLike(commentId, user.uid);

      setComments(prev =>
        prev.map(comment => {
          if (comment.id === commentId) {
            const likedBy = comment.likedBy || [];
            return {
              ...comment,
              likedBy: isLiked
                ? [...likedBy, user.uid]
                : likedBy.filter(id => id !== user.uid),
              likesCount: isLiked
                ? (comment.likesCount || 0) + 1
                : Math.max(0, (comment.likesCount || 0) - 1)
            };
          }
          return comment;
        })
      );
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleEdit = async (commentId: string, content: string) => {
    try {
      await commentService.updateComment(commentId, content);

      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId
            ? { ...comment, content, updatedAt: Timestamp.now() }
            : comment
        )
      );
    } catch (error) {
      console.error('Error editing comment:', error);
      throw error;
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await commentService.deleteComment(commentId);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  const displayedComments = showAll ? comments : comments.slice(0, maxInitialDisplay);
  const hasMore = comments.length > maxInitialDisplay;

  return (
    <div className="space-y-4">
      {/* Comment Count Header */}
      {showCommentCount && (
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <h3 className="font-semibold text-gray-900">
            {comments.length === 0 ? 'No comments yet' : `${comments.length} ${comments.length === 1 ? 'Comment' : 'Comments'}`}
          </h3>
        </div>
      )}

      {/* Add Comment Form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="flex gap-3">
          <div className="flex-shrink-0">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="w-8 h-8 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-sm">
                {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a comment..."
              className="w-full px-4 py-2 border border-gray-300 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
              disabled={isSubmitting}
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={!commentText.trim() || isSubmitting}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {isSubmitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="text-center py-4 bg-gray-50 rounded-xl">
          <p className="text-gray-600 text-sm">
            <a href="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in
            </a>{' '}
            to leave a comment
          </p>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {displayedComments.length > 0 && (
            <div className="space-y-4">
              {displayedComments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  currentUserId={user?.uid}
                  onLike={handleLike}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* Show More Button */}
          {collapsible && hasMore && !showAll && (
            <button
              onClick={() => {
                setShowAll(true);
                if (!hasLoadedComments) {
                  loadComments();
                }
              }}
              className="text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              View all {comments.length} comments
            </button>
          )}
        </>
      )}
    </div>
  );
}
